/**
 * Hook for managing live transcript polling
 */
import { useCallback, useRef } from "react";
import type { UpdateEvent } from "../../../types/domain";
import { extractItems, toUIEvent, mergeStream } from "../lib/transcriptParser";

type UseTranscriptPollingOptions = {
  API: string;
  effectiveSid: string | undefined;
  ensureSession: () => Promise<string | null>;
  getToken: () => Promise<string>;
  setEvents: React.Dispatch<React.SetStateAction<UpdateEvent[]>>;
  setLatestSeq: (seq: number) => void;
  setSessionId?: (sid: string | null) => void;
  onEffectiveSessionId?: (sid?: string) => void;
  setLiveTranscript: (text: string) => void;
  latestSeqRef: React.MutableRefObject<number>;
  lastPublishedSidRef: React.MutableRefObject<string | undefined>;
};

export type StartPollingOpts = {
  /** Stop polling and report failure if no transcript data has EVER arrived within this window. */
  stallTimeoutMs?: number;
  onStallTimeout?: () => void;
  /** Called after several consecutive fetch failures (network or HTTP); polling is stopped first. */
  onPollFailure?: (detail: string) => void;
};

const MAX_CONSECUTIVE_FAILURES = 5;

export function useTranscriptPolling({
  API,
  effectiveSid,
  ensureSession,
  getToken,
  setEvents,
  setLatestSeq,
  setSessionId,
  onEffectiveSessionId,
  setLiveTranscript,
  latestSeqRef,
  lastPublishedSidRef,
}: UseTranscriptPollingOptions) {
  const livePollHandleRef = useRef<number | null>(null);
  const pollingSessionIdRef = useRef<string | null>(null);
  const consecutiveFailuresRef = useRef<number>(0);
  const progressSeenRef = useRef<boolean>(false);
  const pollStartedAtRef = useRef<number>(0);
  const pollOptsRef = useRef<StartPollingOpts>({});

  const stopTranscriptPolling = useCallback(() => {
    if (livePollHandleRef.current != null) {
      window.clearInterval(livePollHandleRef.current);
      livePollHandleRef.current = null;
    }
    pollingSessionIdRef.current = null;
  }, []);

  const startTranscriptPolling = useCallback(async (overrideSessionId?: string, opts?: StartPollingOpts) => {
    try {
      if (!API) return;

      pollOptsRef.current = opts || {};
      consecutiveFailuresRef.current = 0;
      progressSeenRef.current = false;
      pollStartedAtRef.current = Date.now();

      // Use override if provided (avoids closure issues), otherwise fall back to effectiveSid
      let sid: string | undefined = overrideSessionId || effectiveSid;
      if (!sid) sid = (await ensureSession()) ?? undefined;
      if (!sid) return;

      if (!effectiveSid) {
        setSessionId?.(sid as any);
        onEffectiveSessionId?.(sid);
        lastPublishedSidRef.current = sid;
      }

      pollingSessionIdRef.current = sid;

      if (livePollHandleRef.current != null) {
        window.clearInterval(livePollHandleRef.current);
        livePollHandleRef.current = null;
      }

      const registerFailure = (detail: string) => {
        consecutiveFailuresRef.current += 1;
        if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
          console.error("[useTranscriptPolling] giving up after consecutive failures:", detail);
          stopTranscriptPolling();
          pollOptsRef.current.onPollFailure?.(detail);
        }
      };

      const fetchOnce = async () => {
        const currentSid = pollingSessionIdRef.current;
        if (!currentSid) return;
        // Guard: if effectiveSid changed since polling started, discard
        if (effectiveSid && currentSid !== effectiveSid) {
          console.warn("[useTranscriptPolling] session mismatch, stopping stale poll", { currentSid, effectiveSid });
          stopTranscriptPolling();
          return;
        }

        // Stall detection: the processing job never produced anything within the window → it is dead.
        const { stallTimeoutMs, onStallTimeout } = pollOptsRef.current;
        if (stallTimeoutMs && !progressSeenRef.current && Date.now() - pollStartedAtRef.current > stallTimeoutMs) {
          console.error("[useTranscriptPolling] no transcript data within stall window, stopping");
          stopTranscriptPolling();
          onStallTimeout?.();
          return;
        }

        const token = await getToken();
        if (!token) return;

        const afterSeq = latestSeqRef.current || 0;
        const url =
          `${API}/sessions/live_transcript?sessionId=${encodeURIComponent(currentSid)}` +
          `&afterSeq=${encodeURIComponent(String(afterSeq))}`;

        let resp: Response;
        let text = "";
        try {
          resp = await fetch(url, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
          });

          // On 401, refresh token and retry once
          if (resp.status === 401) {
            const freshToken = await getToken();
            if (!freshToken) return;
            resp = await fetch(url, {
              method: "GET",
              headers: { Authorization: `Bearer ${freshToken}`, Accept: "application/json" },
            });
          }

          text = await resp.text();
        } catch (err) {
          console.error("[useTranscriptPolling] live_transcript network error:", err);
          registerFailure(String(err));
          return;
        }

        if (!resp.ok) {
          console.error("[useTranscriptPolling] live_transcript not OK", resp.status, text);
          registerFailure(`HTTP ${resp.status}`);
          return;
        }

        let data: any = {};
        try {
          data = text ? JSON.parse(text) : {};
        } catch (err) {
          console.error("[useTranscriptPolling] live_transcript JSON parse failed:", err);
          registerFailure("invalid JSON response");
          return;
        }

        consecutiveFailuresRef.current = 0;

        const raw = extractItems(data);

        if (raw.length) {
          const incoming: UpdateEvent[] = [];
          for (const b of raw) {
            const ev = toUIEvent(b);
            if (ev) incoming.push(ev);
          }

          if (incoming.length) {
            progressSeenRef.current = true;
            setEvents((prev) => {
              const merged = mergeStream(prev as any, incoming as any);
              const maxSeq = Math.max(0, ...merged.map((e: any) => (typeof e.seq === "number" ? e.seq : 0)));

              const backendLatest =
                typeof data?.latestSeq === "number" && Number.isFinite(data.latestSeq) ? data.latestSeq : 0;

              const newLatest = Math.max(latestSeqRef.current || 0, backendLatest, maxSeq);
              setLatestSeq(newLatest);
              latestSeqRef.current = newLatest;

              return merged as any;
            });
            return;
          }
        }

        const lt =
          typeof data.transcript === "string"
            ? data.transcript
            : typeof data.text === "string"
            ? data.text
            : typeof data.liveTranscript === "string"
            ? data.liveTranscript
            : typeof data.windowTranscript === "string"
            ? data.windowTranscript
            : "";

        if (lt) progressSeenRef.current = true;
        setLiveTranscript(lt);
      };

      await fetchOnce();
      livePollHandleRef.current = window.setInterval(() => void fetchOnce(), 2000);
    } catch (err) {
      console.error("[useTranscriptPolling] startTranscriptPolling error", err);
    }
  }, [
    API,
    effectiveSid,
    ensureSession,
    getToken,
    setEvents,
    setLatestSeq,
    setSessionId,
    onEffectiveSessionId,
    setLiveTranscript,
    latestSeqRef,
    lastPublishedSidRef,
    stopTranscriptPolling,
  ]);

  return {
    livePollHandleRef,
    pollingSessionIdRef,
    startTranscriptPolling,
    stopTranscriptPolling,
  };
}
